/**
 * Cold Email Center - multi-step email/SMS sequences (backed by
 * backend/campaign_routes.py; read campaign_engine.py's docstring for the
 * rules this UI reflects, none of which the UI itself enforces).
 *
 *  - Dashboard: every sequence with real stats (sent / delivered / opened /
 *    clicked / replied / bounced / unsubscribed), computed from tracked
 *    messages - no demo numbers.
 *  - Builder: steps (channel, delay, subject, body), {{token}} help, a live
 *    preview rendered by the server against a sample (or real) lead.
 *  - Approval: submit -> Sales Manager / Master Admin approve or reject ->
 *    activate. Editing approved content sends it back to pending approval.
 *  - Detail: per-step stats, enrollments (stop / mark replied), history.
 *  Enrolling leads happens from the Lead Database selection
 *  (EnrollInSequenceDialog) or by pasting nothing here - leads are chosen
 *  where they are listed.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { AlertCircle, Mail, MessageSquare, Plus, RefreshCw, Trash2 } from 'lucide-react';
import {
  APPROVER_ROLES, STATUS_META, STOP_LABELS, campaignFetch, pct,
} from '@/campaigns/campaignApi';

const EMPTY_STEP = (channel = 'email') => ({ channel, delay_days: channel === 'email' ? 2 : 1, delay_hours: 0, subject: '', body: '' });
const FILTERS = ['all', 'pending_approval', 'draft', 'approved', 'active', 'paused', 'rejected', 'archived'];

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, className: '' };
  return <Badge variant="outline" className={meta.className} data-testid={`sequence-status-${status}`}>{meta.label}</Badge>;
};

const StatsRow = ({ totals }) => (
  <div className="grid grid-cols-3 md:grid-cols-7 gap-2 text-center" data-testid="sequence-stats">
    {[
      ['Sent', totals.sent, null],
      ['Delivered', totals.delivered, totals.rates.delivered],
      ['Opened', totals.opened, totals.rates.opened],
      ['Clicked', totals.clicked, totals.rates.clicked],
      ['Replied', totals.replied, totals.rates.replied],
      ['Bounced', totals.bounced, totals.rates.bounced],
      ['Unsubscribed', totals.unsubscribed, totals.rates.unsubscribed],
    ].map(([label, value, rate]) => (
      <div key={label} className="rounded-md bg-white/5 border border-white/10 p-2">
        <div className="text-xs text-white/60">{label}</div>
        <div className="text-lg font-semibold">{value}</div>
        {rate !== null && <div className="text-xs text-white/60">{pct(rate)}</div>}
      </div>
    ))}
  </div>
);

const ColdEmailSequences = ({ user }) => {
  const token = user.token;
  const isApprover = APPROVER_ROLES.includes(user?.role);
  const [filter, setFilter] = useState('all');
  const [sequences, setSequences] = useState([]);
  const [counts, setCounts] = useState({});
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // full sequence detail
  const [draft, setDraft] = useState(null); // { name, description, steps } being edited
  const [preview, setPreview] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);

  const call = useCallback((path, options) => campaignFetch(token, path, options), [token]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const query = filter === 'all' ? '' : `?status=${filter}`;
      const [list, cfg] = await Promise.all([call(`/campaigns/sequences${query}`), call('/campaigns/settings')]);
      setSequences(list.sequences || []);
      setCounts(list.counts || {});
      setSettings(cfg);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [call, filter]);

  useEffect(() => { loadList(); }, [loadList]);

  const show = useCallback(async (sequence) => {
    setSelected(sequence);
    setDraft({ name: sequence.name, description: sequence.description || '', steps: (sequence.steps || []).map((s) => ({ ...s })) });
    setPreview(null);
    setRejectReason('');
    // The list endpoint omits history and the per-step stats (they are only
    // in the detail response), so a row picked from the list is upgraded to
    // the full sequence - otherwise "Performance by step" and History stay
    // empty until some action happens to reload it.
    if (!sequence.history || !sequence.stats?.steps) {
      call(`/campaigns/sequences/${sequence.id}`)
        .then((full) => setSelected((current) => (current && current.id === full.id ? full : current)))
        .catch(() => { /* keep the list row */ });
    }
    try {
      const data = await call(`/campaigns/sequences/${sequence.id}/enrollments`);
      setEnrollments(data.enrollments || []);
    } catch (error) {
      setEnrollments([]);
    }
  }, [call]);

  const startNew = () => {
    setSelected(null);
    setEnrollments([]);
    setPreview(null);
    setDraft({ name: '', description: '', steps: [EMPTY_STEP('email')] });
  };

  const dirty = useMemo(() => {
    if (!draft) return false;
    if (!selected) return true;
    return JSON.stringify({ n: draft.name, d: draft.description, s: draft.steps.map(({ id, ...rest }) => rest) })
      !== JSON.stringify({ n: selected.name, d: selected.description || '', s: (selected.steps || []).map(({ id, ...rest }) => rest) });
  }, [draft, selected]);

  const run = async (fn, success) => {
    setBusy(true);
    try {
      const data = await fn();
      if (success) toast.success(success);
      return data;
    } catch (error) {
      toast.error(error.message);
      loadList();
      return null;
    } finally {
      setBusy(false);
    }
  };

  const applyResult = (data) => {
    if (!data) return;
    show(data);
    loadList();
  };

  const save = async () => {
    const body = {
      name: draft.name,
      description: draft.description,
      steps: draft.steps.map((s) => ({ ...s, delay_days: Number(s.delay_days) || 0, delay_hours: Number(s.delay_hours) || 0, subject: s.channel === 'email' ? s.subject : '' })),
    };
    if (!selected) {
      applyResult(await run(() => call('/campaigns/sequences', { method: 'POST', body }), 'Sequence saved'));
    } else {
      const wasApproved = ['approved', 'active', 'paused'].includes(selected.status);
      const data = await run(() => call(`/campaigns/sequences/${selected.id}`, { method: 'PATCH', body }), 'Saved');
      if (data && wasApproved && data.status === 'pending_approval') toast.warning('Content changed - this sequence needs approval again before it can send');
      applyResult(data);
    }
  };

  const act = async (path, body, success) => {
    applyResult(await run(() => call(`/campaigns/sequences/${selected.id}/${path}`, { method: 'POST', body }), success));
  };

  const reject = async () => {
    if (!rejectReason.trim()) { toast.error('Give the author a reason'); return; }
    await act('reject', { reason: rejectReason.trim(), version: selected.version }, 'Rejected');
  };

  const archive = async () => {
    if (!window.confirm('Archive this sequence? Every active enrollment stops and it can no longer be used.')) return;
    await act('archive', undefined, 'Archived');
  };

  const refreshPreview = async () => {
    const data = await run(() => call('/campaigns/preview', { method: 'POST', body: { steps: draft.steps.map((s) => ({ ...s, delay_days: Number(s.delay_days) || 0, delay_hours: Number(s.delay_hours) || 0 })) } }));
    if (data) setPreview(data);
  };

  const stopEnrollment = async (enrollment, reason) => {
    const data = await run(() => call(`/campaigns/enrollments/${enrollment.id}/stop`, { method: 'POST', body: { reason } }), reason === 'replied' ? 'Marked replied' : 'Stopped');
    if (data) applyResult(await run(() => call(`/campaigns/sequences/${selected.id}`)));
  };

  const updateStep = (index, patch) => setDraft((d) => ({ ...d, steps: d.steps.map((s, i) => (i === index ? { ...s, ...patch } : s)) }));
  const removeStep = (index) => setDraft((d) => ({ ...d, steps: d.steps.filter((_, i) => i !== index) }));
  const addStep = (channel) => setDraft((d) => ({ ...d, steps: [...d.steps, EMPTY_STEP(channel)] }));

  const status = selected?.status;
  const editable = !selected || !['archived'].includes(status);
  const canAct = selected && (isApprover || selected.created_by_id === user.id);

  return (
    <div className="page-stack revenue-module" data-testid="cold_email-center">
      <section className="module-toolbar glass-panel">
        <div>
          <p className="eyebrow">Revenue Generation Engine</p>
          <h2>Cold Email Center</h2>
          <span>Approved multi-step email/SMS sequences with real send, open, click, reply and bounce tracking.</span>
        </div>
        <div className="flex gap-2">
          <Button className="ghost-btn" onClick={loadList} disabled={loading} data-testid="sequences-refresh"><RefreshCw size={16} /> Refresh</Button>
          <Button className="primary-btn" onClick={startNew} data-testid="sequence-new-button"><Plus size={16} /> New sequence</Button>
        </div>
      </section>

      {settings && (
        <section className="glass-panel text-sm space-y-1" data-testid="sequence-safety-panel">
          <div>
            Sending window: {settings.send_window.days.join(', ')} {settings.send_window.start_hour}:00-{settings.send_window.end_hour}:00 {settings.send_window.timezone}
            {' '}({settings.in_send_window_now ? 'open now' : 'closed now'}) - daily cap {settings.daily_cap}, sent today {settings.sent_today}.
          </div>
          {!settings.providers.sendgrid && <div className="text-amber-300 flex gap-1 items-center"><AlertCircle size={14} /> SendGrid is not configured - email steps will not send (sequences pause with the reason).</div>}
          {!settings.providers.unsubscribe_link_base_url && <div className="text-amber-300 flex gap-1 items-center"><AlertCircle size={14} /> FRONTEND_URL is not set - emails need it for the unsubscribe link.</div>}
          {!settings.providers.event_webhook_verification && <div className="text-amber-300 flex gap-1 items-center"><AlertCircle size={14} /> SendGrid event webhook key is not set - opens, clicks and bounces are not being recorded.</div>}
        </section>
      )}

      <section className="glass-panel">
        <div className="flex flex-wrap gap-2 mb-3">
          {FILTERS.map((f) => (
            <Button key={f} size="sm" className={filter === f ? 'primary-btn' : 'ghost-btn'} onClick={() => setFilter(f)} data-testid={`sequence-filter-${f}`}>
              {f === 'all' ? 'All' : (STATUS_META[f]?.label || f)}{f !== 'all' && counts[f] ? ` (${counts[f]})` : ''}
            </Button>
          ))}
        </div>
        {sequences.length === 0 && !loading && <p className="text-white/60" data-testid="sequences-empty">No sequences yet. Build one, get it approved, then enroll leads from the Lead Database.</p>}
        <div className="space-y-2">
          {sequences.map((s) => (
            <button key={s.id} onClick={() => show(s)} className={`w-full text-left rounded-md border p-3 ${selected?.id === s.id ? 'border-white/40 bg-white/10' : 'border-white/10 bg-white/5'}`} data-testid={`sequence-row-${s.id}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <strong>{s.name}</strong>
                <span className="flex items-center gap-2"><span className="text-xs text-white/60">{s.steps.length} step{s.steps.length === 1 ? '' : 's'} · {s.stats.enrollments.total} enrolled · {s.stats.enrollments.active} active</span><StatusBadge status={s.status} /></span>
              </div>
              <StatsRow totals={s.stats.totals} />
            </button>
          ))}
        </div>
      </section>

      {draft && (
        <section className="glass-panel space-y-4" data-testid="sequence-editor">
          <div className="flex items-center justify-between gap-2">
            <h3>{selected ? selected.name : 'New sequence'} {selected && <StatusBadge status={status} />}</h3>
            {selected && <span className="text-xs text-white/60">v{selected.version}{selected.approved_version ? ` · approved v${selected.approved_version}` : ''}</span>}
          </div>
          {selected?.status === 'rejected' && <p className="text-red-300 text-sm">Rejected: {selected.rejection_reason}</p>}
          {selected?.paused_reason && status === 'paused' && <p className="text-blue-300 text-sm" data-testid="sequence-paused-reason">Paused: {selected.paused_reason}</p>}
          {selected?.status === 'pending_approval' && selected.approved_version && <p className="text-amber-300 text-sm">Content was changed after an earlier approval - nothing sends until this version is approved and re-activated.</p>}

          <div className="grid md:grid-cols-2 gap-2">
            <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Sequence name" disabled={!editable} data-testid="sequence-name-input" />
            <Input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder="Description (optional)" disabled={!editable} />
          </div>

          <p className="text-xs text-white/60">Tokens: {(settings?.tokens || []).map((t) => `{{${t.token}}}`).join(' ')} - add a fallback with {'{{first_name|there}}'}. Missing values use the fallback; values are escaped.</p>

          {draft.steps.map((step, index) => (
            <div key={step.id || index} className="rounded-md border border-white/10 p-3 space-y-2" data-testid={`sequence-step-${index}`}>
              <div className="flex flex-wrap items-center gap-2">
                <strong className="flex items-center gap-1">{step.channel === 'sms' ? <MessageSquare size={14} /> : <Mail size={14} />} Step {index + 1}</strong>
                <select value={step.channel} onChange={(e) => updateStep(index, { channel: e.target.value })} disabled={!editable} className="bg-white/5 border border-white/10 text-white rounded-md px-2 py-1" data-testid={`sequence-step-${index}-channel`}>
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                </select>
                <span className="text-sm text-white/70">{index === 0 ? 'Send after enrollment:' : 'Wait after previous step:'}</span>
                <Input type="number" min="0" max="60" value={step.delay_days} onChange={(e) => updateStep(index, { delay_days: e.target.value })} className="w-20" disabled={!editable} data-testid={`sequence-step-${index}-days`} /> days
                <Input type="number" min="0" max="23" value={step.delay_hours} onChange={(e) => updateStep(index, { delay_hours: e.target.value })} className="w-20" disabled={!editable} /> hours
                {editable && draft.steps.length > 1 && <Button size="sm" className="ghost-btn" onClick={() => removeStep(index)} aria-label={`Remove step ${index + 1}`}><Trash2 size={14} /></Button>}
              </div>
              {step.channel === 'email' && <Input value={step.subject} onChange={(e) => updateStep(index, { subject: e.target.value })} placeholder="Subject" disabled={!editable} data-testid={`sequence-step-${index}-subject`} />}
              <Textarea rows={step.channel === 'sms' ? 3 : 6} value={step.body} onChange={(e) => updateStep(index, { body: e.target.value })} placeholder={step.channel === 'sms' ? 'SMS text ("Reply STOP to opt out." is appended)' : 'Email body - an unsubscribe link and postal address are added automatically'} disabled={!editable} data-testid={`sequence-step-${index}-body`} />
            </div>
          ))}

          {editable && (
            <div className="flex flex-wrap gap-2">
              <Button className="ghost-btn" onClick={() => addStep('email')} disabled={draft.steps.length >= 10}><Plus size={14} /> Email step</Button>
              <Button className="ghost-btn" onClick={() => addStep('sms')} disabled={draft.steps.length >= 10}><Plus size={14} /> SMS step</Button>
              <Button className="ghost-btn" onClick={refreshPreview} disabled={busy} data-testid="sequence-preview-button">Preview with sample lead</Button>
              <Button className="primary-btn" onClick={save} disabled={busy || !dirty || !draft.name.trim()} data-testid="sequence-save-button">Save</Button>
            </div>
          )}

          {preview && (
            <div className="rounded-md bg-white/5 border border-white/10 p-3 space-y-3" data-testid="sequence-preview">
              <p className="text-xs text-white/60">Rendered for {preview.lead.sample ? 'a sample lead' : preview.lead.clinic_name}. Nothing is sent.</p>
              {preview.steps.map((p) => (
                <div key={p.step_index}>
                  <strong>Step {p.step_index + 1} ({p.channel})</strong>
                  {p.subject && <div>Subject: {p.subject}</div>}
                  <pre className="whitespace-pre-wrap text-sm">{p.body}</pre>
                  {p.fallback_tokens.length > 0 && <div className="text-xs text-amber-300">Fallback used for: {p.fallback_tokens.join(', ')}</div>}
                  {p.problems.length > 0 && <div className="text-xs text-red-300">{p.problems.join('; ')}</div>}
                </div>
              ))}
            </div>
          )}

          {selected && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2 items-center" data-testid="sequence-actions">
                {canAct && ['draft', 'rejected'].includes(status) && <Button className="primary-btn" onClick={() => act('submit', undefined, 'Submitted for approval')} disabled={busy || dirty} data-testid="sequence-submit-button">Submit for approval</Button>}
                {isApprover && status === 'pending_approval' && (
                  <>
                    <Button className="primary-btn" onClick={() => act('approve', { version: selected.version }, 'Approved')} disabled={busy || dirty} data-testid="sequence-approve-button">Approve v{selected.version}</Button>
                    <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Rejection reason" className="w-64" />
                    <Button className="ghost-btn" onClick={reject} disabled={busy} data-testid="sequence-reject-button">Reject</Button>
                  </>
                )}
                {status === 'pending_approval' && !isApprover && <span className="text-sm text-amber-300">Waiting for a Sales Manager or Master Administrator to approve.</span>}
                {canAct && ['approved', 'paused'].includes(status) && <Button className="primary-btn" onClick={() => act('activate', undefined, 'Activated')} disabled={busy || dirty} data-testid="sequence-activate-button">{status === 'paused' ? 'Resume' : 'Activate'}</Button>}
                {canAct && status === 'active' && <Button className="ghost-btn" onClick={() => act('pause', undefined, 'Paused')} disabled={busy} data-testid="sequence-pause-button">Pause</Button>}
                {canAct && status !== 'archived' && <Button className="ghost-btn" onClick={archive} disabled={busy}>Archive</Button>}
              </div>
              {['approved', 'active', 'paused'].includes(status) && <p className="text-xs text-white/60">Select leads in the Lead Database and choose "Enroll in sequence".</p>}

              <h4>Performance by step</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="sequence-step-stats">
                  <thead><tr className="text-left text-white/60"><th>Step</th><th>Sent</th><th>Delivered</th><th>Opened</th><th>Clicked</th><th>Replied</th><th>Bounced</th><th>Unsub</th><th>Failed</th></tr></thead>
                  <tbody>
                    {(selected.stats?.steps || []).map((s) => (
                      <tr key={s.step_index}>
                        <td>{s.step_index + 1} · {s.channel}</td><td>{s.sent}</td>
                        <td>{s.delivered} ({pct(s.rates.delivered)})</td><td>{s.opened} ({pct(s.rates.opened)})</td><td>{s.clicked} ({pct(s.rates.clicked)})</td>
                        <td>{s.replied} ({pct(s.rates.replied)})</td><td>{s.bounced} ({pct(s.rates.bounced)})</td><td>{s.unsubscribed}</td><td>{s.failed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-white/60">
                Enrollments: {selected.stats?.enrollments.active} active, {selected.stats?.enrollments.completed} completed, {selected.stats?.enrollments.stopped} stopped
                {Object.keys(selected.stats?.enrollments.stop_reasons || {}).length > 0 && ` (${Object.entries(selected.stats.enrollments.stop_reasons).map(([r, n]) => `${n} ${STOP_LABELS[r] || r}`).join(', ')})`}.
                Opens/clicks need the SendGrid event webhook; open counts can be inflated by mail-client prefetching.
              </p>

              <h4>Enrollments</h4>
              {enrollments.length === 0 && <p className="text-sm text-white/60">No leads enrolled yet.</p>}
              <div className="space-y-1" data-testid="sequence-enrollments">
                {enrollments.map((e) => (
                  <div key={e.id} className="flex flex-wrap items-center justify-between gap-2 rounded bg-white/5 px-3 py-2 text-sm">
                    <span><strong>{e.clinic_name || e.lead_id}</strong> - {e.status}{e.stop_reason ? ` (${STOP_LABELS[e.stop_reason] || e.stop_reason})` : ''}
                      {e.status === 'active' && ` · step ${e.current_step + 1}, next ${e.next_send_at ? new Date(e.next_send_at).toLocaleString() : '-'}`}
                      {e.last_failure_reason && <span className="text-amber-300"> · {e.last_failure_reason}</span>}</span>
                    {e.status === 'active' && (
                      <span className="flex gap-2">
                        <Button size="sm" className="ghost-btn" onClick={() => stopEnrollment(e, 'replied')}>Mark replied</Button>
                        <Button size="sm" className="ghost-btn" onClick={() => stopEnrollment(e, 'manual')}>Stop</Button>
                      </span>
                    )}
                  </div>
                ))}
              </div>

              <details>
                <summary className="cursor-pointer text-sm text-white/70">History ({(selected.history || []).length})</summary>
                <ul className="text-xs text-white/70 mt-2 space-y-1">
                  {(selected.history || []).slice().reverse().map((h) => (
                    <li key={h.id || h.at + h.action}>{new Date(h.at).toLocaleString()} - {h.action.replace(/_/g, ' ')} by {h.by_name || 'system'}{h.reason ? ` (${h.reason})` : ''}</li>
                  ))}
                </ul>
              </details>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default ColdEmailSequences;
